import { useState } from 'react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/custom/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAppDispatch } from '@/redux/hooks'
import { logout } from '@/redux/features/userSlice'
import { useUser } from '@/context/UserContext'
import { Skeleton } from '@/components/ui/skeleton'
import {
  IconBuildingWarehouse,
  IconLanguage,
  IconAlertTriangle,
  IconBell,
  IconCheck,
  IconChevronDown,
  IconLogout,
  IconMoon,
  IconPrinter,
  IconSun,
} from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import useModal from '@/hooks/use-modal'
import PrinterSettingsDialog from './printer-settings-dialog'
import { clearRedirectAfterLogin } from '@/utils/redirect-after-login'
import { clearRouteParamsMemory } from '@/utils/route-params-memory'
import { useTheme } from '@/components/theme-provider'
import useChangeLanguage from '@/hooks/use-change-language'
import { LANGS } from './lang-options'
import { Switch } from '@/components/ui/switch'
import { useNotificationsContext } from '@/context/NotificationsContext'

function getInitials(firstName: string, lastName: string) {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
}

export function UserNav() {
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const { t, i18n } = useTranslation()
  const { theme, setTheme } = useTheme()
  const [langOpen, setLangOpen] = useState(false)
  const changeLanguage = useChangeLanguage()
  const { user, isLoading } = useUser()
  const printerSettingsModal = useModal()
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false)
  const { notificationsEnabled, updateSettings } = useNotificationsContext()

  const initials = user ? getInitials(user.first_name, user.last_name) : '??'
  const fullName = user ? `${user.first_name} ${user.last_name}` : ''

  const handleLogout = () => {
    // Signing out on purpose is not an expired session — drop any remembered
    // target so the next login starts from the home page, and forget the per-page
    // filters so the next person to sign in on this tab does not inherit them.
    clearRedirectAfterLogin()
    clearRouteParamsMemory()
    dispatch(logout())
    queryClient.removeQueries({ queryKey: ['user-me'] })
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='ghost' className='relative h-8 w-8 rounded-full'>
            <Avatar className='h-8 w-8'>
              <AvatarFallback className='bg-primary/10 text-xs font-semibold text-primary'>
                {isLoading ? (
                  <Skeleton className='h-full w-full rounded-full' />
                ) : (
                  initials
                )}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className='w-64' align='end' forceMount>
          <DropdownMenuLabel className='p-0'>
            <div className='flex items-center gap-3 p-3'>
              <Avatar className='h-10 w-10'>
                <AvatarFallback className='bg-primary/10 text-sm font-semibold text-primary'>
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className='flex flex-col gap-1'>
                <p className='text-sm font-semibold leading-none'>{fullName}</p>
                <p className='text-xs text-muted-foreground'>
                  {user?.username}
                </p>
              </div>
            </div>
          </DropdownMenuLabel>

          {user?.working_warehouses?.map((ww, idx) => (
            <div key={idx}>
              <DropdownMenuSeparator />
              <div className='space-y-1 px-3 py-2'>
                <p className='text-xs font-medium text-foreground'>{ww.role}</p>
                <div className='flex flex-col gap-1'>
                  {ww.for_all_warehouses ? (
                    <div className='flex items-center gap-1.5 text-xs text-muted-foreground'>
                      <IconBuildingWarehouse className='h-3 w-3 shrink-0' />
                      <span>{t('user_nav.all_warehouses')}</span>
                    </div>
                  ) : (
                    ww.warehouses.map((warehouse) => (
                      <div
                        key={warehouse.id}
                        className='flex items-center gap-1.5 text-xs text-muted-foreground'
                      >
                        <IconBuildingWarehouse className='h-3 w-3 shrink-0' />
                        <span className='truncate'>{warehouse.name}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          ))}

          <div>
            <DropdownMenuSeparator />
            {/* Below sm: inline toggle group (sideways submenus clip on phones) */}
            <div className='sm:hidden'>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault()
                  setLangOpen((prev) => !prev)
                }}
                aria-expanded={langOpen}
              >
                <IconLanguage className='mr-2 h-4 w-4' />
                <span className='flex-1'>{t('user_nav.language')}</span>
                <IconChevronDown
                  className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`}
                />
              </DropdownMenuItem>
              {langOpen &&
                LANGS.map((lang) => (
                  <DropdownMenuItem
                    key={lang.value}
                    className='cursor-pointer gap-2.5 pl-8'
                    onClick={() => changeLanguage(lang.value)}
                  >
                    <lang.flag className='h-4 w-4' />
                    <span
                      className={`flex-1 ${lang.value === i18n.language ? 'font-semibold' : ''}`}
                    >
                      {lang.label}
                    </span>
                    {lang.value === i18n.language && (
                      <IconCheck className='h-4 w-4 text-primary' />
                    )}
                  </DropdownMenuItem>
                ))}
            </div>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className='max-sm:hidden'>
                <IconLanguage className='mr-2 h-4 w-4' />
                {t('user_nav.language')}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent collisionPadding={8}>
                {LANGS.map((lang) => (
                  <DropdownMenuItem
                    key={lang.value}
                    className='cursor-pointer gap-2.5'
                    onClick={() => changeLanguage(lang.value)}
                  >
                    <lang.flag className='h-4 w-4' />
                    <span
                      className={
                        lang.value === i18n.language ? 'font-semibold' : ''
                      }
                    >
                      {lang.label}
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuItem
              onSelect={(e) => e.preventDefault()}
              className='cursor-default'
            >
              <IconBell className='mr-2 h-4 w-4' />
              <span className='flex-1'>{t('notifications.title')}</span>
              <Switch
                checked={notificationsEnabled}
                onCheckedChange={updateSettings}
                aria-label={t('notifications.title')}
              />
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault()
                setTheme(theme === 'light' ? 'dark' : 'light')
              }}
            >
              {theme === 'light' ? (
                <IconMoon className='mr-2 h-4 w-4' />
              ) : (
                <IconSun className='mr-2 h-4 w-4' />
              )}
              {t('user_nav.theme')}
            </DropdownMenuItem>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault()
              requestAnimationFrame(() => printerSettingsModal.open())
            }}
          >
            <IconPrinter className='mr-2 h-4 w-4' />
            {t('user_nav.printer_settings')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault()
              requestAnimationFrame(() => setLogoutConfirmOpen(true))
            }}
            className='text-destructive focus:text-destructive'
          >
            <IconLogout className='mr-2 h-4 w-4' />
            {t('user_nav.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <PrinterSettingsDialog modal={printerSettingsModal} />
      <Dialog open={logoutConfirmOpen} onOpenChange={setLogoutConfirmOpen}>
        <DialogContent className='max-w-lg'>
          <div className='flex flex-col items-center gap-5 px-2 py-4 text-center'>
            <div className='flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-950/40'>
              <IconAlertTriangle
                size={36}
                className='text-red-600 dark:text-red-400'
              />
            </div>
            <div className='space-y-2'>
              <DialogTitle className='text-xl font-semibold'>
                {t('user_nav.logout_confirm_title')}
              </DialogTitle>
              <DialogDescription className='text-sm leading-relaxed'>
                {t('user_nav.logout_confirm_description')}
              </DialogDescription>
            </div>
          </div>
          <DialogFooter className='flex gap-2 sm:flex-row'>
            <Button
              variant='outline'
              className='flex-1'
              onClick={() => setLogoutConfirmOpen(false)}
            >
              {t('global:cancel')}
            </Button>
            <Button
              variant='destructive'
              className='flex-1'
              onClick={() => {
                setLogoutConfirmOpen(false)
                handleLogout()
              }}
            >
              {t('user_nav.logout')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
